/** Blocking confirm for exec-pricing escapes, styled in the app's toast visual
 *  language (see ToastContainer.jsx). Promise-based so escape flows can await
 *  the decision; replaces window.confirm (CONF-106 part 2 escape-audit spec §5). */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const TITLE_ID = 'exec-confirm-dialog-title';

export function ExecConfirmDialog({ artifactLabel, changeCount, metricCount = 0, onContinue, onCancel }) {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  // Ref-mirrored so the mount-only effect below can always call the CURRENT
  // onCancel (identity changes every render, see useExecConfirmDialog) without
  // re-running focus/listener setup on every prop change.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    dialogRef.current?.querySelector('button')?.focus();
    const onKeyDown = (e) => { if (e.key === 'Escape') onCancelRef.current(); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, []);

  return ReactDOM.createPortal(
    <div
      aria-label="Close dialog"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1a1500',
          border: '1px solid #3d3000',
          borderTop: '3px solid #E79F23',
          borderRadius: '8px',
          padding: '16px 18px',
          width: '340px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.7)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '9px' }}>
          <span style={{
            background: '#E79F23', color: '#000', fontSize: '9px', fontWeight: 700,
            padding: '2px 6px', borderRadius: '10px', letterSpacing: '0.5px', flexShrink: 0,
          }}>
            EXEC PRICING
          </span>
          <span id={TITLE_ID} style={{ fontSize: '12px', fontWeight: 600, color: '#E79F23' }}>
            {metricCount === 0 ? 'Executive price changes' : (changeCount === 0 ? 'Executive metric overrides' : 'Executive changes')}
          </span>
        </div>
        <p style={{ fontSize: '11px', color: '#bbb', lineHeight: 1.45, margin: '0 0 12px' }}>
          {(() => {
            const parts = [];
            if (changeCount > 0) parts.push(`${changeCount} total executive price change${changeCount === 1 ? '' : 's'}`);
            if (metricCount > 0) parts.push(`${metricCount} metric override${metricCount === 1 ? '' : 's'}`);
            return parts.join(' and ');
          })()} will be included in this {artifactLabel}. Continue?
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onCancel}
            style={{
              background: 'none', border: '1px solid #3d3000', color: '#bbb',
              borderRadius: '6px', padding: '5px 12px', fontSize: '11px', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onContinue}
            style={{
              background: '#E79F23', border: 'none', color: '#000', fontWeight: 700,
              borderRadius: '6px', padding: '5px 12px', fontSize: '11px', cursor: 'pointer',
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function useExecConfirmDialog() {
  const [req, setReq] = useState(null); // { artifactLabel, changeCount, metricCount, resolve }

  const confirmExecEscape = useCallback(({ artifactLabel, changeCount, metricCount = 0 }) =>
    new Promise((resolve) => setReq((prev) => {
      prev?.resolve(false);                       // cancel any pending request — never orphan an await
      return { artifactLabel, changeCount, metricCount, resolve };
    })), []);

  const settle = useCallback((answer) => {
    setReq((prev) => { prev?.resolve(answer); return null; });
  }, []);

  const dialogElement = req ? (
    <ExecConfirmDialog
      artifactLabel={req.artifactLabel}
      changeCount={req.changeCount}
      metricCount={req.metricCount}
      onContinue={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null;

  return { dialogElement, confirmExecEscape };
}
